import React from "react";

/**
 * CalorieSilhouettesModal
 *
 * Visualizes total calories as a set of human silhouettes, filled bottom-up.
 * Example: perSilhouette = 2000 kcal, total = 7000 kcal -> 3 full + 1 half.
 */
export default function CalorieSilhouettesModal({
                                                    open = true,
                                                    onClose = () => {},
                                                    totalCalories = 7000,
                                                    perSilhouette = 2000,
                                                    title = "Kalorijų vizualizacija",
                                                    inline = false,
                                                }) {
    const totalUnits = totalCalories / perSilhouette; // e.g., 3.5
    const full = Math.floor(totalUnits);
    const partial = totalUnits - full; // 0..1

    // Build an array of fill ratios for each silhouette
    const fills = Array.from({ length: full }, () => 1);
    if (partial > 0) fills.push(Number(partial.toFixed(4)));

    // Add at least one silhouette even if total < perSilhouette
    if (fills.length === 0) fills.push(0);

    if (inline) {
        return (
            <div className="w-full rounded-2xl bg-white">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <h3 className="text-xl font-semibold tracking-tight">
                            {title}
                        </h3>
                        <p className="mt-1 text-sm text-gray-600">
                            Iš viso: <span className="font-medium">{totalCalories.toLocaleString()} kcal</span> ·
                            Vienam siluetui: <span className="font-medium">{perSilhouette.toLocaleString()} kcal</span>
                        </p>
                    </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-6 sm:grid-cols-3 md:grid-cols-4">
                    {fills.map((ratio, i) => (
                        <SilhouetteCard key={i} index={i + 1} fillRatio={ratio} />
                    ))}
                </div>
                <div className="mt-4 rounded-xl bg-gray-50 p-4 text-sm text-gray-700">
                    <p>
                        Kiekvienas žmogaus siluetas atitinka {perSilhouette.toLocaleString()} kcal.
                        Užpildymo aukštis rodo dalį nuo vieno silueto – pildoma iš apačios į viršų.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <>
            {open && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-6"
                    role="dialog"
                    aria-modal="true"
                >
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/40"
                        onClick={onClose}
                        aria-hidden
                    />

                    {/* Modal card */}
                    <div
                        className="relative z-10 w-full max-w-3xl rounded-2xl bg-white p-6 shadow-2xl"
                    >
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h2 className="text-2xl font-semibold tracking-tight">
                                    {title}
                                </h2>
                                <p className="mt-1 text-sm text-gray-600">
                                    Iš viso: <span className="font-medium">{totalCalories.toLocaleString()} kcal</span> ·
                                    Vienam siluetui: <span className="font-medium">{perSilhouette.toLocaleString()} kcal</span>
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                                aria-label="Uždaryti"
                                title="Uždaryti"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" aria-hidden="true">
                                    <path fillRule="evenodd" d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>

                        {/* Silhouettes grid */}
                        <div className="mt-6 grid grid-cols-2 gap-6 sm:grid-cols-3 md:grid-cols-4">
                            {fills.map((ratio, i) => (
                                <SilhouetteCard key={i} index={i + 1} fillRatio={ratio} />
                            ))}
                        </div>

                        {/* Legend / helper text */}
                        <div className="mt-6 rounded-xl bg-gray-50 p-4 text-sm text-gray-700">
                            <p>
                                Kiekvienas žmogaus siluetas atitinka {perSilhouette.toLocaleString()} kcal.
                                Užpildymo aukštis rodo dalį nuo vieno silueto – pildoma iš apačios į viršų.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

/**
 * SVG person silhouette with bottom-up fill using a clipPath.
 * The drawing uses a stable viewBox (0 0 100 160) so we can compute clip heights easily.
 */
function SilhouetteCard({ index, fillRatio }) {
    const vbWidth = 100;
    const vbHeight = 160; // must match viewBox height
    const height = vbHeight * Math.max(0, Math.min(1, fillRatio));
    const y = vbHeight - height;
    const clipId = `fill-clip-${index}`;

    return (
        <div className="flex flex-col items-center rounded-2xl border p-4">
            <svg
                viewBox={`0 0 ${vbWidth} ${vbHeight}`}
                className="h-36 w-auto"
                aria-labelledby={`title-${index}`}
                role="img"
            >
                <title id={`title-${index}`}>Siluetas #{index} – užpildymas {(fillRatio * 100).toFixed(0)}%</title>

                {/* Define the clipping rectangle for the fill */}
                <clipPath id={clipId}>
                    <rect x="0" y={y} width={vbWidth} height={height} />
                </clipPath>

                {/* Base silhouette outline */}
                <g stroke="#1f2937" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    {/* head */}
                    <circle cx="50" cy="22" r="14" />
                    {/* neck */}
                    <path d="M44 36 L56 36 L56 44 L44 44 Z" />
                    {/* torso */}
                    <rect x="32" y="44" width="36" height="42" rx="10" />
                    {/* arms */}
                    <path d="M32 52 L18 80" />
                    <path d="M68 52 L82 80" />
                    {/* hips */}
                    <rect x="36" y="86" width="28" height="18" rx="8" />
                    {/* legs */}
                    <path d="M42 104 L36 152" />
                    <path d="M58 104 L64 152" />
                    {/* feet */}
                    <path d="M32 152 L44 152" />
                    <path d="M56 152 L68 152" />
                </g>

                {/* Filled silhouette using same geometry but clipped */}
                <g clipPath={`url(#${clipId})`}>
                    {/* We redraw the silhouette paths, but filled */}
                    <g fill="#10b981">
                        <circle cx="50" cy="22" r="14" />
                        <rect x="44" y="36" width="12" height="8" />
                        <rect x="32" y="44" width="36" height="42" rx="10" />
                        <rect x="36" y="86" width="28" height="18" rx="8" />
                        {/* Approximate arms/legs with thicker paths as filled rectangles */}
                        <rect x="28" y="60" width="8" height="24" transform="rotate(-60 32 72)" />
                        <rect x="64" y="60" width="8" height="24" transform="rotate(60 68 72)" />
                        <rect x="38" y="104" width="8" height="52" transform="rotate(8 42 130)" />
                        <rect x="54" y="104" width="8" height="52" transform="rotate(-8 58 130)" />
                        <rect x="32" y="150" width="12" height="4" />
                        <rect x="56" y="150" width="12" height="4" />
                    </g>
                </g>
            </svg>

            <div className="mt-2 text-center text-sm">
                <div className="font-medium">{(fillRatio * 100).toFixed(0)}%</div>
                <div className="text-gray-500">Siluetas #{index}</div>
            </div>
        </div>
    );
}

/**
 * USAGE EXAMPLE (drop-in):
 *
 * <CalorieSilhouettesModal
 *   open={isOpen}
 *   onClose={() => setOpen(false)}
 *   totalCalories={7000}
 *   perSilhouette={2000}
 * />
 *
 * Notes / Extensibility:
 * - Animate the fill by animating the clip rect's y/height with Framer Motion.
 * - To enforce a maximum number of silhouettes, slice the `fills` array.
 * - For accessibility, the <title> on each SVG exposes the fill percentage to screen readers.
 * - You can swap the simple silhouette for an imported SVG path if you have a designer asset.
 */
